'use client';

import { c as _c } from "react/compiler-runtime";
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { useEffect, useRef, useState, type CSSProperties, type RefObject } from 'react';
import { ArrowLeft } from 'lucide-react';
import { CollectionStage, type SpreadState, type StageMode } from '@/lib/bricks/stage/collection-stage';
import { makeViewport, spreadRect } from '@/lib/bricks/stage/reading';
import { useMountEffect } from '@/hooks/useMountEffect';
import { typing } from '@/lib/bricks/keys';
import { displayYear } from '@/lib/bricks/year';
import { SERIES, type Series } from '@/lib/bricks/series';
import type { ShelfSet } from '@/lib/bricks/sets';
import { loadBrickSet } from '@/lib/bricks/sets-client';
import { boxArtFor } from '@/lib/bricks/boxes';
import { modelUrl } from '@/lib/bricks/sets-client';
import { Generator } from './Generator';
import type { SpreadRect } from './Reader';
import { useStageView, StageSwitch, type Stage } from './ViewToggle';
import '@/app/bricks/viewer.css';
const loadReader = () => import('./Reader');
const loadBuildViewer = () => import('./BuildViewer');
const loadBoxStage = () => import('./BoxStage');
const Reader = dynamic(loadReader, {
  ssr: false
});
const BuildViewer = dynamic(loadBuildViewer, {
  ssr: false
});
const BoxStage = dynamic(loadBoxStage, {
  ssr: false
});

// The .ldr text isn't parsed here (BrickKit needs the part geometries
// alongside it, and re-running that work would just be waste) — this only
// gets the bytes into the HTTP cache, so the eventual `fetch` in
// model-viewer.ts's loadModel resolves from disk instead of the network.
function preloadModel(slug: string) {
  fetch(modelUrl(slug)).catch(() => {});
}

// Warm the reader's code, a set's part geometry and its build data (steps,
// parts, notes) before a booklet opens, so opening one is just showing
// what's already loaded rather than starting the fetches. The 3D view is
// one click away from an open booklet, so its chunk and model go warm here
// too — most of the visible latency on first open of `?view=3d` is this
// same waterfall (chunk, .ldr, parts.bin) paid cold.
async function preloadReader(slug: string | null) {
  if (slug) preloadModel(slug);
  const [,,, library] = await Promise.all([loadReader().catch(() => null), loadBuildViewer().catch(() => null), loadBoxStage().catch(() => null), slug ? import('@/lib/bricks/runtime/library').catch(() => null) : null, slug ? loadBrickSet(slug).catch(() => null) : null]);
  if (slug) await library?.loadPartGeometries(slug).catch(() => null);
}

// Once a series has finished standing up: loading the reader in the same
// frames stalls that move.
function preloadSoon(slug: string | null) {
  window.setTimeout(() => {
    const idle = window.requestIdleCallback ?? ((fn: () => void) => fn());
    idle(() => void preloadReader(slug));
  }, 1400);
}

// The booklet opens on a photo of the original; fetch it before it's asked for.
function preloadPhoto(set: ShelfSet) {
  if (set.photo) new Image().src = set.photo;
}

// The box's six faces and flat dieline, once the booklet has settled open,
// so the box view opens on a cached box rather than waiting on its
// textures.
function preloadBox(slug: string) {
  const art = boxArtFor(slug);
  if (!art) return;
  window.setTimeout(() => {
    for (const src of [...Object.values(art.faces), art.flat]) new Image().src = src;
  }, 2500);
}

// A link to another set inside the open booklet ("more in this series")
// moves the stage there rather than loading the page again; a click meant
// for a new tab keeps the browser's own handling.
function followSetLink(e: MouseEvent, stage: CollectionStage, sets: ShelfSet[]) {
  if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  const link = e.target instanceof Element ? e.target.closest<HTMLElement>('a[data-set]') : null;
  if (!link) return;
  const index = sets.findIndex(s => s.slug === link.dataset.set);
  if (stage.showOther(index)) e.preventDefault();
}

// The open booklet's other two stages, its finished build in 3D (?view=3d)
// and its box (?view=box).
function urlFor(mode: StageMode, series: Series | undefined, set?: ShelfSet, stage: Stage = 'book') {
  if (mode === 'table' || !series) return '/bricks';
  if (mode === 'shelf' || !set) return `/bricks/${series.key}`;
  const url = `/bricks/${series.key}/${set.slug}`;
  if (mode !== 'reading') return url;
  if (stage === 'model') return `${url}?view=3d`;
  if (stage === 'box') return `${url}?view=box`;
  return `${url}?view=build`;
}
function readUrl(series: Series[], sets: ShelfSet[]): {
  mode: StageMode;
  series: number;
  index: number;
  stage: Stage;
} {
  const [, key, slug] = window.location.pathname.match(/^\/bricks(?:\/([^/]+))?(?:\/([^/]+))?/) ?? [];
  const s = series.findIndex(x => x.key === key);
  if (s < 0) return {
    mode: 'table',
    series: -1,
    index: -1,
    stage: 'book'
  };
  const index = slug ? sets.findIndex(x => x.slug === slug) : -1;
  if (index < 0) return {
    mode: 'shelf',
    series: s,
    index: -1,
    stage: 'book'
  };
  const view = new URLSearchParams(window.location.search).get('view');
  const reading = view === 'build' || view === '3d' || view === 'box';
  return {
    mode: reading ? 'reading' : 'carousel',
    series: s,
    index,
    stage: view === '3d' ? 'model' : view === 'box' ? 'box' : 'book'
  };
}
const here = () => window.location.pathname + window.location.search;
function measureSpread(): SpreadRect {
  const r = spreadRect(makeViewport(window.innerWidth, window.innerHeight));
  return {
    left: r.left,
    top: r.top,
    width: r.width,
    height: r.height,
    single: r.single
  };
}

// Where a server-rendered page starts. The server can't see ?view=build;
// the stage corrects it on mount.
function landing(sets: ShelfSet[], series: Series[], initialSeries: string | null, initialSet: string | null) {
  const startSeries = series.findIndex(s => s.key === initialSeries);
  const startSet = initialSet ? sets.findIndex(s => s.slug === initialSet) : -1;
  const startMode: StageMode = startSet >= 0 ? 'carousel' : startSeries >= 0 ? 'shelf' : 'table';
  return {
    startSeries,
    startSet,
    startMode
  };
}
const BACK_LABEL: Record<Exclude<StageMode, 'table'>, string> = {
  shelf: 'Back to all the series',
  carousel: 'Back to the shelf',
  reading: 'Close the booklet'
};
function BackButton(t0) {
  const $ = _c(6);
  const {
    mode,
    stage,
    onBack
  } = t0;
  if (mode === "table") {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Link href="/craft" aria-label="Back to craft" title="Back to craft"><ArrowLeft size={15} /></Link>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  const label = stage !== "book" ? "Back to the booklet" : BACK_LABEL[mode];
  const t1 = `${label} (Esc)`;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ArrowLeft size={15} />;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== label || $[3] !== onBack || $[4] !== t1) {
    t3 = <button type="button" onClick={onBack} aria-label={label} aria-keyshortcuts="Escape" title={t1}>{t2}</button>;
    $[2] = label;
    $[3] = onBack;
    $[4] = t1;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}

// What a screen reader hears as the view changes.
function announce(mode: StageMode, set: ShelfSet, series: Series | undefined) {
  if (mode === 'reading' || mode === 'carousel') return `${set.name}, ${set.maker} ${displayYear(set.year)}`;
  return mode === 'shelf' && series ? series.name : '';
}

// What the next step will need, fetched ahead of it.
function warmFor(mode: StageMode, set: ShelfSet | undefined) {
  if (mode === 'shelf') preloadSoon(null);
  if (mode === 'carousel' && set) preloadSoon(set.slug);
  if (mode === 'reading' && set) {
    preloadPhoto(set);
    preloadBox(set.slug);
  }
}
function moveTo(url: string, how: 'push' | 'replace') {
  if (here() === url) return;
  if (how === 'push') window.history.pushState({
    bricks: true
  }, '', url);else window.history.replaceState({
    bricks: true
  }, '', url);
}

// Back and forward: walk the stage to wherever the address now points.
function followHistory(stage: CollectionStage, target: ReturnType<typeof readUrl>) {
  if (target.mode === 'table') {
    stage.closeBook();
    stage.toShelf();
    stage.toTable();
  }
  if (target.mode === 'shelf') {
    stage.closeBook();
    stage.toShelf();
    if (stage.mode === 'table') stage.openSeries(target.series);
  }
  if (target.mode === 'carousel') {
    if (stage.mode === 'reading') stage.closeBook();else stage.showBook(target.index);
  }
  if (target.mode === 'reading') stage.openBook(target.index);
}

// Keys the open booklet would act on underneath the 3D view.
const BOOKLET_KEYS = new Set(['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', ' ']);
function wheelStage(stage: CollectionStage, e: WheelEvent) {
  if (stage.mode === 'reading') return;
  e.preventDefault();
  const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
  stage.wheel(delta * (e.deltaMode === 1 ? 32 : 1));
}

// Global keys: Escape steps back through whichever of the model or box
// stage is up, then the collection stage itself; V and B swap to those two
// stages; the reader and the stage handle everything else, once the stage
// above them is out of the way.
function keyHandler(e: KeyboardEvent, stage: CollectionStage, view: ReturnType<typeof useStageView>, openSlug: () => string) {
  if (typing(e)) return;
  if (e.key === 'Escape') {
    if (view.isOpen()) view.showBook(false);else stage.back();
  }
  // This listener goes on before the reader's, so it can keep the hidden
  // booklet from turning its pages under the model or box stage.
  if (view.isOpen() && BOOKLET_KEYS.has(e.key)) {
    e.stopImmediatePropagation();
    e.preventDefault();
    return;
  }
  const plain = !e.metaKey && !e.ctrlKey && !e.altKey;
  const key = plain ? e.key.toLowerCase() : '';
  if (stage.mode === 'reading' && key === 'v') {
    if (view.stage === 'model') view.showBook(false);else view.showModel(view.stage === 'book' ? 'push' : 'replace');
  }
  if (stage.mode === 'reading' && key === 'b' && boxArtFor(openSlug())) {
    if (view.stage === 'box') view.showBook(false);else view.showBox(view.stage === 'book' ? 'push' : 'replace');
  }
  if (stage.mode !== 'reading') stage.key(e.key);
}

// Back and forward: read whichever stage the address now points at and
// show it without touching history again (it's already where the pop
// landed).
function popHandler(series: Series[], sets: ShelfSet[], stage: CollectionStage, view: ReturnType<typeof useStageView>, fromHistory: RefObject<boolean>) {
  const target = readUrl(series, sets);
  // Between the booklet and its model/box stage, the collection stage
  // stays put.
  if (target.mode === 'reading' && stage.mode === 'reading') {
    if (target.stage === 'model') view.showModel('none');else if (target.stage === 'box') view.showBox('none');else view.showBook(true);
    return;
  }
  fromHistory.current = true;
  followHistory(stage, target);
  fromHistory.current = false;
  if (target.mode === 'reading' && target.stage === 'model') view.showModel('none');else if (target.mode === 'reading' && target.stage === 'box') view.showBox('none');
}

// A booklet and the page it's open at.
type Place = {
  slug: string;
  page: string;
};

// The /bricks experience: every series as a stack on a table, a series stood
// up on its shelf, a booklet brought forward in the carousel and opened from
// there. The stage draws all of it in WebGL; this component owns the DOM
// around it and keeps the URL in step (/bricks, /bricks/<series>,
// /bricks/<series>/<set>, and ?view=build once it's open) with pushState, so
// any link lands where it was shared from.
export default function Library({
  sets,
  initialSeries = null,
  initialSet = null
}: {
  sets: ShelfSet[];
  initialSeries?: string | null;
  initialSet?: string | null;
}) {
  // Only series with sets in them make the table.
  const [series] = useState(() => SERIES.filter(s => sets.some(set => set.series === s.key)));
  const seriesIndex = (set_0: ShelfSet) => series.findIndex(s_0 => s_0.key === set_0.series);
  const {
    startSeries,
    startSet,
    startMode
  } = landing(sets, series, initialSeries, initialSet);
  const [mode, setMode] = useState<StageMode>(startMode);
  const [focus, setFocus] = useState(() => Math.max(0, startSet));
  // The booklet open (or last opened): it stays the reader's while it
  // shuts, even as the carousel moves on to another.
  const [opened, setOpened] = useState(() => Math.max(0, startSet));
  const [openSeries, setOpenSeries] = useState(startSeries);
  const [spread, setSpread] = useState<SpreadRect | null>(null);
  const [spreadState, setSpreadState] = useState<SpreadState>('closed');
  // The page the open booklet is on, so it reopens there when the reader
  // remounts for a new layout (a phone turned sideways).
  const [place, setPlace] = useState<Place | null>(null);
  const fromHistory = useRef(false);
  const view = useStageView();
  const {
    stage: stageView,
    viewing
  } = view;
  const [stage] = useState(() => new CollectionStage(sets.map(s_1 => ({
    slug: s_1.slug,
    series: seriesIndex(s_1),
    cover: s_1.cover,
    spine: '#f4f4f1',
    tone: s_1.tone,
    paper: s_1.paper,
    front: s_1.front,
    name: s_1.name
  })), series.length, {
    onMode: (next, s_2, index) => {
      setMode(next);
      setOpenSeries(s_2);
      if (index >= 0) setFocus(index);
      if (next !== 'reading') view.drop();
      // The reader mounts as the booklet starts to lift, so its code,
      // model and part geometry load on the way.
      if (next === 'reading') {
        setOpened(index);
        setSpread(measureSpread());
        setPlace(null);
      }
      const set_1 = index >= 0 ? sets[index] : undefined;
      warmFor(next, set_1);
      if (!fromHistory.current) moveTo(urlFor(next, series[s_2], set_1), 'push');
    },
    // The carousel moving on keeps the address on its booklet without
    // a history entry per booklet.
    onFocus: index_0 => {
      setFocus(index_0);
      const set_2 = sets[index_0]!;
      warmFor('carousel', set_2);
      moveTo(urlFor('carousel', series[seriesIndex(set_2)], set_2), 'replace');
    },
    onSpread: setSpreadState
  }));
  const host = useRef<HTMLDivElement>(null);
  const root = useRef<HTMLElement>(null);
  // The mount-once effect below closes over this on the first render; keep
  // it live so its `B` shortcut always reads the set actually open.
  const focusedRef = useRef(sets[opened]);
  useEffect(() => {
    focusedRef.current = sets[opened];
  });
  useMountEffect(() => {
    const node = host.current;
    if (!node) return;
    const landing = readUrl(series, sets);
    stage.start(landing.mode, landing.series, landing.index);
    setMode(landing.mode);
    if (landing.mode === 'reading') setSpread(measureSpread());
    if (landing.mode === 'reading' && landing.stage === 'model') view.showModel('none');else if (landing.mode === 'reading' && landing.stage === 'box') view.showBox('none');
    stage.attach(node);
    // The scrim behind an open booklet fades to the page's own colour.
    const paint = () => {
      if (root.current) stage.setBackground(getComputedStyle(root.current).backgroundColor);
    };
    paint();
    const scheme = window.matchMedia('(prefers-color-scheme: dark)');
    const theme = new MutationObserver(paint);
    theme.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-theme', 'style']
    });
    scheme.addEventListener('change', paint);
    const onResize = () => {
      stage.resize();
      setSpread(s_3 => s_3 ? measureSpread() : s_3);
    };
    const onKey = (e: KeyboardEvent) => keyHandler(e, stage, view, () => focusedRef.current!.slug);
    // Non-passive, so the wheel scrolls the shelf without scrolling the page.
    const onWheel = (e_0: WheelEvent) => wheelStage(stage, e_0);
    const onPop = () => popHandler(series, sets, stage, view, fromHistory);
    const onClick = (e_1: MouseEvent) => followSetLink(e_1, stage, sets);
    const page = root.current;
    page?.addEventListener('click', onClick);
    node.addEventListener('wheel', onWheel, {
      passive: false
    });
    window.addEventListener('resize', onResize);
    window.addEventListener('keydown', onKey);
    window.addEventListener('popstate', onPop);
    return () => {
      stage.detach();
      theme.disconnect();
      scheme.removeEventListener('change', paint);
      node.removeEventListener('wheel', onWheel);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('popstate', onPop);
      page?.removeEventListener('click', onClick);
    };
  });
  const focused = sets[focus]!;
  const book = sets[opened]!;
  const shownSeries = series[openSeries];
  const boxArt = boxArtFor(book.slug);
  return <main ref={root} className="bricks-library" data-mode={mode} data-view={stageView} {...view.coverProps} style={{
    '--set-accent': focused.palette.accent,
    '--set-cover': focused.palette.cover
  } as CSSProperties}>
      <div ref={host} className="bricks-stage" onPointerDown={e_2 => {
      if (e_2.button !== 0) return;
      stage.pointerDown(e_2.clientX, e_2.clientY, e_2.pointerType !== 'touch');
    }} onPointerMove={e_3 => stage.pointerMove(e_3.clientX, e_3.clientY, e_3.pointerType !== 'touch')} onPointerUp={e_4 => stage.pointerUp(e_4.clientX, e_4.clientY)} onPointerCancel={e_5 => stage.pointerUp(e_5.clientX, e_5.clientY)} onPointerLeave={() => stage.pointerLeave()} />

      <header className="bricks-header">
        <BackButton mode={mode} stage={stageView} onBack={() => {
        if (view.isOpen()) view.showBook(false);else stage.back();
      }} />
      </header>

      {/* Keyboard and screen-reader access to what the canvas draws. */}
      {/* Out of the way while a booklet is open: its buttons would act on
          the stage underneath it. */}
      <nav className="sr-only" aria-label="Series" hidden={mode === 'reading'}>
        {series.map((s_4, si) => <section key={s_4.key}>
            <button type="button" onClick={() => stage.openSeries(si)}>
              {s_4.name}
            </button>
            <ul>
              {sets.map((set_3, index_1) => ({
            set: set_3,
            index: index_1
          })).filter(({
            set: set_4
          }) => set_4.series === s_4.key).map(({
            set: set_5,
            index: index_2
          }) => <li key={set_5.slug}>
                    <button type="button" onClick={() => stage.openBook(index_2)}>
                      {set_5.name}, {set_5.maker} {displayYear(set_5.year)}.{' '}
                      {set_5.pieces} pieces. Open booklet
                    </button>
                  </li>)}
            </ul>
          </section>)}
      </nav>

      <p className="sr-only" aria-live="polite">
        {announce(mode, focused, shownSeries)}
      </p>

      {/* Hidden rather than unmounted off the table: a generation already
          under way has spent its slot of the daily cap, and unmounting
          would throw its result away. */}
      <div hidden={mode !== 'table'}>
        <Generator sets={sets} onReveal={slug => {
        const index_3 = sets.findIndex(s_5 => s_5.slug === slug);
        if (index_3 < 0) return;
        stage.openSeries(seriesIndex(sets[index_3]!));
        window.setTimeout(() => stage.openBook(index_3), 1400);
      }} />
      </div>

      {/* Kept mounted for the whole reading session once opened once
          (view.modelMounted), hidden rather than torn down between toggles
          so switching back to the booklet and into 3D again doesn't rebuild
          the WebGLRenderer and BrickKit from scratch — see BuildViewer's
          `hidden` prop and ModelViewer.suspend(). A real set change still
          gets a fresh instance: the key is the open set's slug. */}
      {mode === 'reading' && view.modelMounted && <BuildViewer key={`model-${book.slug}`} slug={book.slug} leaving={stageView !== 'model'} hidden={!viewing} onViewer={view.setModelViewer} />}

      {/* Same idea for the box: mounted once, suspended off-screen rather
          than torn down between toggles, and kept up under its replacement
          until that has faded in (view.boxShown). */}
      {mode === 'reading' && boxArt && view.boxMounted && <BoxStage key={`box-${book.slug}`} art={boxArt} name={book.name} hidden={!view.boxShown} leaving={stageView !== 'box'} onViewer={view.setBoxViewer} />}

      {/* Stays mounted while the cover swings shut, so its pages go as
          the cover comes back over them. */}
      {(mode === 'reading' || spreadState !== 'closed') && spread && <Reader key={`${book.slug}-${spread.single}`} slug={book.slug} allSets={sets} rect={spread} shown={spreadState} closing={mode !== 'reading'} resume={place?.slug === book.slug ? place.page : null} onInside={canvas => stage.setInside(opened, canvas)} onPage={page_0 => setPlace({
      slug: book.slug,
      page: page_0
    })} onClose={() => stage.closeBook()} onBare={bare => stage.setBare(bare)} onRuntime={view.setRuntime} />}

      {/* After the booklet, so the keyboard reaches them from its pages. */}
      {mode === 'reading' && <div className="bricks-control-dock">
          <StageSwitch stage={stageView} hasBox={!!boxArt} onSelect={next_0 => {
        if (next_0 === stageView) return;
        if (next_0 === 'book') view.showBook(false);else if (next_0 === 'model') view.showModel(stageView === 'book' ? 'push' : 'replace');else view.showBox(stageView === 'book' ? 'push' : 'replace');
      }} />
        </div>}
    </main>;
}